import calls from '@/backend/api-calls'

const state = () => ({
  estimation: null,
  firstLoad: true,
  loading: false,
  estimationSummary: null,
  summaryFirstLoad: true,
  summaryLoading: false,
  summaryParams: {
    pricing_type: 'mpa'
  },
  blockSaving: false,
  draft: null,
  draftSavedAt: null
})

// getters
const getters = {
  isMitrex: state => state.estimation.rfq.product_brand == 'Mitrex',
  estimationType: (state, getters) => {
    return getters.isMitrex ? 'mitrexEstimation' : 'cladifyEstimation' 
  },
  estimationItemElevation: (state, getters) => { 
    return getters.isMitrex ? 'estimationItemElevationMitrex' : 'estimationItemElevation' 
  },
  estimationItemElevations: (state, getters) => { 
    return getters.isMitrex ? 'estimationItemElevationsMitrex' : 'estimationItemElevations' 
  }
}

// actions
const actions = {
  getEstimation: ({commit}, id) => {
    commit("setLoading", true);

    return new Promise((resolve, reject) => {
      calls.cladifyEstimation.get(id)
        .then(resp => {
          commit("setEstimation", resp.data);
          commit("setLoading", false);
          commit("setFirstLoad", false);
          commit("setBlockSaving", false);
          resolve(resp);
        }).catch(err => {
          commit("setLoading", false);
          commit("setBlockSaving", false);
          reject(err);
        })
    })
  },
  updateEstimation: ({commit, state}, { estimationId, data, replaceWithResponse=true }) => {
    return new Promise((resolve, reject) => {
      calls.cladifyEstimation.put(estimationId, data)
        .then(resp => {
          const newEstimation = replaceWithResponse 
            ? resp.data
            : {...state.estimation, ...data}
          commit("setEstimation", newEstimation);
          commit("app/setNotification", {
            type: 'success',
            status: true,
            message: 'Estimaton successfully updated'
          }, { root: true });
          
          resolve(resp);
        }).catch(err => {
          reject(err);
        })
    })
  },
  addItemElevation: ({commit, getters}, elevation) => {
    return new Promise((resolve, reject) => {
      calls[getters.estimationItemElevations].post(elevation).then(
        resp => {
          commit("insertEstimationElevation", resp.data);
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  updateItemElevation: ({commit, getters}, { itemElevationId, data, field }) => {
    return new Promise((resolve, reject) => {
      calls[getters.estimationItemElevation].put(itemElevationId, data).then(
        resp => {
          commit("insertUpdatedElevation", resp.data, field);
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  deleteItemElevation: ({commit, getters}, itemElevationId) => {
    return new Promise((resolve, reject) => {
      calls[getters.estimationItemElevation].delete(itemElevationId).then(
        resp => {
          commit("removeElevation", itemElevationId);
          commit("app/setNotification", {
            type: 'success',
            status: true,
            message: 'Estimation item elevation information successfully deleted'
          }, { root: true });
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  updateElevationOrders: ({commit, getters}, data) => {
    return new Promise((resolve, reject) => {
      calls[getters.estimationItemElevations].updateOrders(data).then(
        resp => {
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  addItemInstallation: ({commit}, installation) => {
    commit('setBlockSaving', true);
    return new Promise((resolve, reject) => {
      calls.estimationItemInstallations.post(installation).then(
        resp => {
          commit("insertEstimationInstallation", resp.data);
          resolve(resp);
        }
      ).catch(err => {
        commit('setBlockSaving', false);
        reject(err)
      });
    })
  },
  updateItemInstallation: ({commit, dispatch}, { estimationId, itemInstallationId, data, update=true }) => {
    commit('setBlockSaving', true);
    return new Promise((resolve, reject) => {
      calls.estimationItemInstallation.put(itemInstallationId, data).then(
        resp => {
          if (update) {
            dispatch("getEstimation", estimationId);
          }
          resolve(resp);
        }
      ).catch(err => {
        commit('setBlockSaving', false);
        reject(err)
      });
    })
  },
  updateEstimator: ({commit}, { rfqId, data }) => {
    return new Promise((resolve, reject) => {
      calls.cladifyRfq.put(rfqId, data).then(
        resp => {
          commit("insertUpdatedRfq", resp.data);
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  updateItemMaterial({commit, dispatch}, {estimationId, itemMaterialId, data, update=true}) {
    return new Promise((resolve, reject) => {
      calls.estimationItemMaterial.put(itemMaterialId, data).then(
        resp => {
          if (update) {
            dispatch("getEstimation", estimationId);
          }
          commit("insertUpdatedItemMaterial", resp.data);
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  updateItemProcessing({dispatch}, {estimationId, itemProcessingId, data, update=true}) {
    return new Promise((resolve, reject) => {
      calls.estimationItemProcessing.put(itemProcessingId, data).then(
        resp => {
          if (update) {
            dispatch("getEstimation", estimationId);
          }
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  updateItemFabrication({dispatch}, {estimationId, itemFabricationId, data, update=true}) {
    return new Promise((resolve, reject) => {
      calls.estimationItemFabrication.put(itemFabricationId, data).then(
        resp => {
          if (update) {
            dispatch("getEstimation", estimationId);
          }
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  updateService({dispatch}, {estimationId, serviceId, data}) {
    return new Promise((resolve, reject) => {
      calls.estimationService.put(serviceId, data).then(
        resp => {
          dispatch("getEstimation", estimationId);
          resolve(resp);
        }
      ).catch(err => reject(err));
    })
  },
  // Mitrex Estimation Summary
  getEstimationSummary: ({commit}, { id, params }) => {
    commit("setSummaryLoading", true);

    return new Promise((resolve, reject) => {
      calls.estimationSummaryMitrex.get(id, params)
        .then(resp => {
          commit("setEstimationSummary", resp.data);
          commit("setSummaryLoading", false);
          commit("setSummaryFirstLoad", false);
          resolve(resp);
        }).catch(err => {
          commit("setSummaryLoading", false);
          reject(err);
        })
    })
  },
  adjustSummaryPrice: ({commit}, { estimationId, data, params }) => {
    return new Promise((resolve, reject) => {
      calls.estimationSummaryMitrex.post(estimationId, data, params)
        .then(resp => {
          commit("setEstimationSummary", resp.data);
          commit("setNewSummary", resp.data);
          resolve(resp);
        }).catch(err => {
          commit("setSummaryLoading", false);
          reject(err);
        })
    })
  },
  updateInsulation: ({commit, state}, { insulationId, data, replaceWithResponse=true }) => {
    return new Promise((resolve, reject) => {
      calls.estimationInsulation.put(insulationId, data)
        .then(resp => {
          const newInsulation = replaceWithResponse
            ? resp.data
            : {...state.estimation.insulation, ...data}
          commit("setNewInsulation", newInsulation);
          resolve(resp);
        }).catch(err => {
          reject(err);
        })
    })
  },
  saveDraft: ({commit, getters}, { estimationId, data }) => {
    return new Promise((resolve, reject) => {
      calls[getters.estimationType].saveDraft(estimationId, data)
        .then(resp => {
          commit("setDraftData", data);
          commit("setDraftSavedAt", new Date());
          resolve(resp);
        }).catch(err => {
          reject(err);
        })
    })
  },
  getDraft: ({commit, getters}, estimationId) => {
    return new Promise((resolve, reject) => {
      calls[getters.estimationType].getDraft(estimationId)
        .then(resp => {
          commit("setCurrentDraft", resp.data);
          resolve(resp);
        }).catch(err => {
          reject(err);
        })
    })
  }
}

// mutations
const mutations = {
  setFirstLoad(state, status) {
    state.firstLoad = status;
  },
  setLoading(state, status) {
    state.loading = status;
  },
  setEstimation(state, data) {
    state.estimation = data;
    state.estimation.items = state.estimation.items.sort((a,b) => {
      if(a.order == b.order) {
        return +a.id - +b.id;
      } else {
        return +a.order - +b.order;
      }
    });
    state.estimation.items.forEach(item => {
      item.materials.sort((a,b) => +b.product_master.product_category_id -
        +a.product_master.product_category_id);
      item.processing.sort((a,b) => +a.processing.processing_type_id - 
        +b.processing.processing_type_id);
      item.fabrications.sort((a,b) => +a.fabrication.fabrication_type_id - 
        +b.fabrication.fabrication_type_id);
      item.installations.sort((a,b) => +a.installation.installation_type_id - 
        +b.installation.installation_type_id);
    });
    state.estimation.services.sort((a,b) => +a.service.id - +b.service.id);

    if (state.estimation.rfq.drawing_highlights.length > 0) {
      state.estimation.rfq.drawing_highlights[0].files.sort((a,b) => +a.id - +b.id);
    }
  },
  insertEstimationElevation(state, elevation) {
    state.estimation.items.forEach(item => {
      if (elevation.estimation_item_id == item.id) {
        item.elevations.push(elevation);
      }
    })
  },
  insertUpdatedElevation(state, elevation, field) {
    state.estimation.items.forEach(item => {
      if (item.id == elevation.estimation_item_id) {
        let ind = item.elevations.map(d => d.id).indexOf(elevation.id);
        if (field) {
          const newData = Object.fromEntries([[field, elevation[field]]])
          item.elevations.splice(ind, 1, {...item.elevations[ind], ...newData})
        } else {
          item.elevations.splice(ind, 1, elevation);
        }
      }
    })
  },
  removeElevation(state, itemElevationId) {
    state.estimation.items.forEach(item => {
      item.elevations.forEach((elevation, i) => {
        if (elevation.id == itemElevationId) {
          item.elevations.splice(i, 1);
        }
      })
    });
  },
  insertEstimationInstallation(state, installation) {
    state.estimation.items.forEach(item => {
      if (installation.estimation_item_id == item.id) {
        item.installations.push(installation);
      }
    })
  },
  insertUpdatedInstallation(state, installation) {
    state.estimation.items.forEach(item => {
      if (item.id == installation.estimation_item_id) {
        let ind = item.installations.map(d => d.id).indexOf(installation.id);
        item.installations.splice(ind, 1, installation);
      }
    })
  },
  insertUpdatedItemMaterial(state, data) {
    state.estimation.items.forEach(item => {
      if (item.id == data.estimation_item_id) {
        item.materials.forEach((material, i) => {
          if (material.id == data.id) {
            item.materials.splice(i, 1, data);
          }
        })
      }
    })
  },
  insertUpdatedRfq(state, data) {
    state.estimation.rfq = data;
  },
  // Estimation Summary
  setSummaryFirstLoad(state, status) {
    state.summaryFirstLoad = status;
  },
  setSummaryLoading(state, status) {
    state.summaryLoading = status;
  },
  setEstimationSummary(state, data) {
    state.estimationSummary = data;
  },
  setNewSummary(state, data) {
    if (state.summaryParams.pricing_type == 'mpa') {
      state.estimation.estimation_summary = data;
    } else {
      state.estimation.estimation_summary_dpa = data;
    }
  },
  setNewInsulation(state, data) {
    state.estimation.insulation = data;
  },
  setSummaryParams(state, { field, value }) {
    state.summaryParams[field] = value;
  },
  setBlockSaving(state, value) {
    state.blockSaving = value;
  },
  setDraftData(state, value) {
    state.draft.data = value;
  },
  setCurrentDraft(state, value) {
    state.draft = value;
  },
  setDraftSavedAt(state, value) {
    state.draftSavedAt = value;
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}